import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import {
  MAT_LUXON_DATE_ADAPTER_OPTIONS,
  MatLuxonDateModule,
} from '@angular/material-luxon-adapter';
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialogModule,
} from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
  NoPreloading,
  provideRouter,
  TitleStrategy,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import {
  AuthService,
  authHttpInterceptorFn,
  provideAuth0,
} from '@auth0/auth0-angular';
import { provideTransloco } from '@jsverse/transloco';
import { filter, take } from 'rxjs';
import { getApiBaseUrl, getAuthConfig } from '@/app/core/api/api-base-url';
import { authRecoveryInterceptor } from '@/app/core/auth/auth-recovery.interceptor';
import { AuthRedirectService } from '@/app/core/auth/auth-redirect.service';
import { API_BASE_URL } from '@/app/core/auto-generated/api.service';
import { errorInterceptor } from '@/app/core/http/error.interceptor';
import { provideIcons } from '@/app/core/icons/provider';
import { loadingInterceptor } from '@/app/core/loading/loading.interceptor';
import { AppTitleStrategy } from '@/app/core/routing/app-title.strategy';
import { PwaService } from '@/app/core/services/pwa.service';
import { SessionStore } from '@/app/core/state/session-store';
import {
  USER_SETTINGS_IDENTITY,
  UserSettingsStore,
} from '@/app/core/state/user-settings-store';
import { LoggingErrorHandler } from '@/app/core/telemetry/logging-error-handler.service';
import { PosthogIdentityService } from '@/app/core/telemetry/posthog-identity.service';
import { RouteAnalyticsService } from '@/app/core/telemetry/route-analytics.service';
import { provideTheming } from '@/app/core/theming';
import { STANDARD_COLORS } from '@/app/core/theming/standard-colors';
import { TranslocoHttpLoader } from '@/app/core/transloco/transloco-http-loader';
import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: USER_SETTINGS_IDENTITY,
      useFactory: () => inject(SessionStore),
    },
    provideBrowserGlobalErrorListeners(),
    // Keep lazy features on demand instead of importing them all in the background.
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      withPreloading(NoPreloading)
    ),
    { provide: TitleStrategy, useClass: AppTitleStrategy },

    // HTTP + API client (NSwag api.service.ts) ----------------------------
    provideHttpClient(
      withFetch(),
      withInterceptors([
        loadingInterceptor,
        errorInterceptor,
        authRecoveryInterceptor,
        authHttpInterceptorFn,
      ])
    ),
    {
      // Base url the generated CustomersClient (and siblings) prepend to every

      provide: API_BASE_URL,
      useFactory: getApiBaseUrl,
    },

    // Auth0 — attaches the access token to API requests via the interceptor.
    provideAuth0(getAuthConfig()),

    // Errors + telemetry
    {
      provide: ErrorHandler,
      useClass: LoggingErrorHandler,
    },
    provideAppInitializer(() => {
      inject(PosthogIdentityService).initialize();
    }),
    provideAppInitializer(() => {
      inject(RouteAnalyticsService).initialize();
    }),
    provideAppInitializer(() => {
      inject(AuthRedirectService).initialize();
    }),
    provideAppInitializer(() => {
      inject(PwaService).initialize();
    }),
    provideAppInitializer(() => {
      // Bridge the Auth0 token streams into SessionStore once authenticated, so
      // the user's claims + permissions are available app-wide as signals. The
      // loader stays subscribed afterward, so token refresh / logout keep the
      // store in sync. Never blocks bootstrap on the request itself.
      const authService = inject(AuthService);
      const store = inject(SessionStore);
      authService.isAuthenticated$
        .pipe(filter(Boolean), take(1))
        .subscribe(() => store.load());
    }),
    provideAppInitializer(() => {
      // Load every persisted user setting once, as soon as Auth0 has a token
      // (the API requires it), so list screens can restore their saved paging
      // and sort synchronously from UserSettingsStore. Returns after wiring the
      // subscription — it never blocks bootstrap on the request itself.
      const authService = inject(AuthService);
      const store = inject(UserSettingsStore);
      authService.isAuthenticated$
        .pipe(filter(Boolean), take(1))
        .subscribe(() => store.load());
    }),

    // Material
    importProvidersFrom(MatLuxonDateModule, MatDialogModule),
    {
      // Native popover positioning can resolve against the wrong coordinate
      // space inside scrollable dialogs. Keep connected overlays in the CDK
      // overlay container so selects remain anchored to their trigger.
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
    {
      // Every dialog wears the StatusNow modal chrome — see
      // styles/components/modal.css (the one place to change modal styling).
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        panelClass: 'sn-dialog',
        backdropClass: 'sn-dialog-backdrop',
        autoFocus: false,
        maxWidth: '94vw',
      },
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic',
      },
    },
    {
      provide: MAT_LUXON_DATE_ADAPTER_OPTIONS,
      useValue: { useUtc: true },
    },

    // Core
    provideIcons(),
    provideTheming({
      scheme: 'light',
      ...STANDARD_COLORS,
    }),

    // Third-party
    // Chart.js is provided by `sn-chart` (shared/ui/charts) so it loads with
    // the lazy chart features instead of the initial bundle.
    provideTransloco({
      config: {
        availableLangs: [
          {
            id: 'en',
            label: 'English',
          },
          {
            id: 'es',
            label: 'Español',
          },
        ],
        defaultLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
  ],
};
